import { FC, lazy, Suspense, useContext, useState } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// Components
import { PdpSharedIconBubblePinterest } from '../PdpSharedIconBubbles/PdpSharedIconBubblePinterest';
import { PdpSharedIconBubbleFacebook } from '../PdpSharedIconBubbles/PdpSharedIconBubbleFacebook';
import { PdpSharedIconBubbleEmail } from '../PdpSharedIconBubbles/PdpSharedIconBubbleEmail';
import { PdpRespIconBubbleShare } from './PdpRespIconBubbleShare';

import { PdpRespIconBubbles_viewer$data } from './__generated__/PdpRespIconBubbles_viewer.graphql';
import { PdpRespIconBubbles_item$data } from './__generated__/PdpRespIconBubbles_item.graphql';
import { PdpRespIconBubbles_user$data } from './__generated__/PdpRespIconBubbles_user.graphql';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import styles from './PdpRespIconBubbles.scss';

const PdpRespShareIconTooltipLazy = lazy(
    () => import(/* webpackChunkName: "PdpRespShareIconTooltip" */ './PdpRespShareIconTooltip')
);

type Props = {
    viewer: PdpRespIconBubbles_viewer$data;
    item: PdpRespIconBubbles_item$data;
    user: PdpRespIconBubbles_user$data | null;
    skuId?: string;
    isClient?: boolean;
};

const PdpTabletIconBubbles: FC<Props> = ({ item, skuId, user, viewer, isClient }) => {
    const { isJewelry } = item;
    const { shareJewelryItemTooltip } = viewer;
    const [isShareTooltipVisible, setIsShareTooltipVisible] = useState(false);

    const getEmailIcon = (): JSX.Element => (
        <PdpSharedIconBubbleEmail item={item} user={user} viewer={viewer} skuId={skuId} />
    );

    return (
        <>
            <PdpSharedIconBubblePinterest item={item} />
            <PdpSharedIconBubbleFacebook item={item} />
            {isClient && isJewelry && shareJewelryItemTooltip ? (
                <div
                    className={styles.tooltipWrapper}
                    onMouseEnter={() => setIsShareTooltipVisible(true)}
                    onMouseLeave={() => setIsShareTooltipVisible(false)}
                >
                    {isShareTooltipVisible && (
                        <Suspense fallback="">
                            <PdpRespShareIconTooltipLazy />
                        </Suspense>
                    )}
                    {getEmailIcon()}
                </div>
            ) : (
                getEmailIcon()
            )}
        </>
    );
};

const PdpRespIconBubblesComponent: FC<Props> = props => {
    const { isDesktop } = useContext(ServerVarsContext);
    return isDesktop ? <PdpRespIconBubbleShare {...props} /> : <PdpTabletIconBubbles {...props} />;
};

export const PdpRespIconBubbles = createFragmentContainer(PdpRespIconBubblesComponent, {
    viewer: graphql`
        fragment PdpRespIconBubbles_viewer on Viewer {
            ...PdpSharedIconBubbleEmail_viewer
            ... @include(if: $fetchPdp) {
                shareJewelryItemTooltip: featureFlag(feature: "shareJewelryItemTooltip")
            }
            ...PdpRespIconBubbleShare_viewer
        }
    `,
    item: graphql`
        fragment PdpRespIconBubbles_item on Item {
            isJewelry
            ...PdpSharedIconBubblePinterest_item
            ...PdpSharedIconBubbleFacebook_item
            ...PdpSharedIconBubbleEmail_item
            ...PdpRespIconBubbleShare_item
        }
    `,
    user: graphql`
        fragment PdpRespIconBubbles_user on User {
            ...PdpSharedIconBubbleEmail_user
            ...PdpRespIconBubbleShare_user
        }
    `,
});
